import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Wallet, RefreshCw, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

/**
 * UnappliedPayerCreditsCard — Phase 4 widget for unapplied PLB credits.
 *
 * Shows PLB segments (provider-level balances from 835s) that arrived as
 * money NOT yet attributed to a specific claim. Billers can apply a credit
 * to a claim, which posts a matching event on `claim_payment_events` and
 * flips the PLB event to applied.
 */
export const UnappliedPayerCreditsCard = () => {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const [applyDialog, setApplyDialog] = useState({
    open: false,
    credit: null,
    claimId: '',
    amount: '',
    notes: '',
  });
  const [applyBusy, setApplyBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/payer-credits/unapplied`);
      setCredits(resp.data?.credits || []);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || 'Failed to load payer credits');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openApply = (credit) => {
    setApplyDialog({
      open: true,
      credit,
      claimId: '',
      amount: String(Math.abs(Number(credit.remaining_amount ?? credit.amount ?? 0)).toFixed(2)),
      notes: '',
    });
  };

  const apply = async () => {
    const { credit, claimId, amount, notes } = applyDialog;
    if (!credit) return;
    if (!claimId.trim()) { toast.error('Claim ID required'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Valid amount required'); return; }
    setApplyBusy(true);
    try {
      await axios.post(`${API}/payer-credits/${credit.id}/apply`, {
        claim_id: claimId.trim(),
        amount: amt,
        notes: notes || null,
      });
      toast.success(`Applied ${fmtMoney(amt)} to claim`);
      setApplyDialog({ open: false, credit: null, claimId: '', amount: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply credit');
    } finally {
      setApplyBusy(false);
    }
  };

  const totalOutstanding = credits.reduce(
    (sum, c) => sum + Math.abs(Number(c.remaining_amount ?? c.amount ?? 0)),
    0,
  );

  return (
    <Card className="border-l-4 border-l-blue-500" data-testid="unapplied-payer-credits-card">
      <CardHeader className="cursor-pointer pb-3" onClick={() => setCollapsed((c) => !c)}>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-blue-600" />
            Unapplied Payer Credits
            <Badge
              className={`${credits.length > 0 ? 'bg-blue-600' : 'bg-gray-400'} text-white`}
              data-testid="unapplied-credits-count"
            >
              {credits.length}
            </Badge>
            {totalOutstanding > 0 && (
              <span className="text-sm font-mono text-blue-700">
                {fmtMoney(totalOutstanding)}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="unapplied-credits-refresh-btn"
              className="h-8"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      {!collapsed && (
        <CardContent>
          {loading && credits.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
          )}
          {!loading && credits.length === 0 && (
            <div
              className="text-center py-6 text-sm text-gray-500"
              data-testid="unapplied-credits-empty"
            >
              No unapplied PLB credits. Payer-level balances from ERAs will appear here when they aren't already tied to a specific claim.
            </div>
          )}
          {credits.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="unapplied-credits-table">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="text-left p-2">Payer</th>
                    <th className="text-left p-2">PLB Code</th>
                    <th className="text-left p-2">Check / ERA</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-right p-2">Remaining</th>
                    <th className="text-left p-2">Posted</th>
                    <th className="text-right p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {credits.map((c) => (
                    <tr
                      key={c.id}
                      className="border-t hover:bg-blue-50/30"
                      data-testid={`unapplied-credit-row-${c.id}`}
                    >
                      <td className="p-2">
                        <div>{c.payer_name || '—'}</div>
                        <div className="text-[10px] text-gray-400 font-mono">NPI {c.provider_npi}</div>
                      </td>
                      <td className="p-2">
                        <div className="font-mono text-xs">{c.plb_reason_code || '—'}</div>
                        <div className="text-[10px] text-gray-400 capitalize">
                          {(c.plb_category || '').replace(/_/g, ' ')}
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="font-mono text-xs">{c.check_number || '—'}</div>
                        <div className="text-[10px] text-gray-400">{fmtDate(c.check_date)}</div>
                      </td>
                      <td className="p-2 text-right font-mono">{fmtMoney(c.amount)}</td>
                      <td className="p-2 text-right font-mono font-semibold text-blue-700">
                        {fmtMoney(c.remaining_amount ?? c.amount)}
                      </td>
                      <td className="p-2 text-xs text-gray-600">{fmtDate(c.posted_at)}</td>
                      <td className="p-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => openApply(c)}
                          data-testid={`unapplied-credit-apply-btn-${c.id}`}
                        >
                          <Check className="h-3 w-3 mr-1" /> Apply
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}

      <Dialog open={applyDialog.open} onOpenChange={(open) => !open && setApplyDialog({ open: false, credit: null, claimId: '', amount: '', notes: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Payer Credit to Claim</DialogTitle>
            <DialogDescription>
              {applyDialog.credit && (
                <>
                  {applyDialog.credit.payer_name} · PLB {applyDialog.credit.plb_reason_code} ·{' '}
                  Remaining <span className="font-mono text-blue-700">
                    {fmtMoney(applyDialog.credit.remaining_amount ?? applyDialog.credit.amount)}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Claim ID</Label>
              <Input
                value={applyDialog.claimId}
                onChange={(e) => setApplyDialog((d) => ({ ...d, claimId: e.target.value }))}
                placeholder="claim UUID"
                className="font-mono text-xs"
                data-testid="apply-credit-claim-id-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Paste the claim's internal ID (from the Claim Detail dialog or Client Chart Billing tab).
              </p>
            </div>
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={applyDialog.amount}
                onChange={(e) => setApplyDialog((d) => ({ ...d, amount: e.target.value }))}
                data-testid="apply-credit-amount-input"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={applyDialog.notes}
                onChange={(e) => setApplyDialog((d) => ({ ...d, notes: e.target.value }))}
                placeholder="e.g., Applied to cover Feb 10 session"
                data-testid="apply-credit-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApplyDialog({ open: false, credit: null, claimId: '', amount: '', notes: '' })}
              disabled={applyBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={apply}
              disabled={applyBusy}
              data-testid="apply-credit-confirm-btn"
            >
              {applyBusy ? 'Applying…' : 'Apply Credit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UnappliedPayerCreditsCard;
